import React, { createContext, useContext, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const PreloadContext = createContext(null);

export const usePreload = () => {
  const context = useContext(PreloadContext);
  if (!context) {
    throw new Error('usePreload must be used within PreloadProvider');
  }
  return context;
};

export const PreloadProvider = ({ children }) => {
  const { authFetch, isAuthenticated } = useAuth();
  
  // Cache for preloaded data
  const cache = useRef({
    splitGroups: null,
    splitGroupsTimestamp: 0,
    splitGroupDetails: {},
  });
  
  // Track ongoing requests to prevent duplicates
  const pendingRequests = useRef({});
  
  // Cache duration (30 seconds)
  const CACHE_DURATION = 30000;
  
  // Preload split groups list
  const preloadSplitGroups = useCallback(async () => {
    if (!isAuthenticated) return null;
    
    const now = Date.now();
    
    // Return cached data if still valid
    if (cache.current.splitGroups && now - cache.current.splitGroupsTimestamp < CACHE_DURATION) {
      return cache.current.splitGroups;
    }
    
    // Prevent duplicate requests
    if (pendingRequests.current.splitGroups) {
      return pendingRequests.current.splitGroups;
    }
    
    // Start fetching
    pendingRequests.current.splitGroups = (async () => {
      try {
        const response = await authFetch('/api/split/groups');
        if (response.ok) {
          const data = await response.json();
          cache.current.splitGroups = data.groups || [];
          cache.current.splitGroupsTimestamp = Date.now();
          return cache.current.splitGroups;
        }
      } catch (error) {
        console.error('Preload split groups failed:', error);
      } finally {
        pendingRequests.current.splitGroups = null;
      }
      return null;
    })();
    
    return pendingRequests.current.splitGroups;
  }, [authFetch, isAuthenticated]);
  
  // Preload a specific split group's details
  const preloadSplitGroupDetails = useCallback(async (groupId) => {
    if (!isAuthenticated || !groupId) return null;
    
    const now = Date.now();
    const cacheKey = `group_${groupId}`;
    
    // Return cached data if still valid
    const cached = cache.current.splitGroupDetails[groupId];
    if (cached && now - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    
    // Prevent duplicate requests
    if (pendingRequests.current[cacheKey]) {
      return pendingRequests.current[cacheKey];
    }
    
    // Start fetching
    pendingRequests.current[cacheKey] = (async () => {
      try {
        const response = await authFetch(`/api/split/groups/${groupId}`);
        if (response.ok) {
          const data = await response.json();
          cache.current.splitGroupDetails[groupId] = {
            data,
            timestamp: Date.now()
          };
          return data;
        }
      } catch (error) {
        console.error('Preload group details failed:', error);
      } finally {
        pendingRequests.current[cacheKey] = null;
      }
      return null;
    })();
    
    return pendingRequests.current[cacheKey];
  }, [authFetch, isAuthenticated]);
  
  // Get cached split groups (synchronous - returns null if not cached)
  const getCachedSplitGroups = useCallback(() => {
    const now = Date.now();
    if (cache.current.splitGroups && now - cache.current.splitGroupsTimestamp < CACHE_DURATION) {
      return cache.current.splitGroups;
    }
    return null;
  }, []);
  
  // Get cached group details (synchronous)
  const getCachedGroupDetails = useCallback((groupId) => {
    const now = Date.now();
    const cached = cache.current.splitGroupDetails[groupId];
    if (cached && now - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }, []);
  
  // Invalidate split cache (call after adding expense, settling up, etc.)
  const invalidateSplitCache = useCallback((groupId = null) => {
    if (groupId) {
      delete cache.current.splitGroupDetails[groupId];
    } else {
      cache.current.splitGroups = null;
      cache.current.splitGroupsTimestamp = 0;
      cache.current.splitGroupDetails = {};
    }
  }, []);
  
  const value = {
    preloadSplitGroups,
    preloadSplitGroupDetails,
    getCachedSplitGroups,
    getCachedGroupDetails,
    invalidateSplitCache,
  };
  
  return (
    <PreloadContext.Provider value={value}>
      {children}
    </PreloadContext.Provider>
  );
};
